import { useState } from "react";
import { Plus } from "lucide-react";
import { useLang } from "@/lib/adminLang";
import { nok, kompakt, STAGE_LABEL, MODE_LABEL } from "./budsjettUtils";
import TicketModal from "./TicketModal";
import SponsorModal from "./SponsorModal";
import LineDrawer from "./LineDrawer";

const SectionHead = ({ idx, title, meta, onAdd, addLabel, addTestid }) => (
  <div className="flex flex-wrap items-end justify-between mb-4 gap-x-3 gap-y-2">
    <div className="min-w-0">
      <h2 className="text-[16px] font-semibold tracking-tight text-neutral-900">
        <span className="bud-mono text-[10px] font-normal text-neutral-400 mr-2.5 tracking-[0.14em]">{idx}</span>
        {title}
      </h2>
      {meta && <p className="text-[11px] text-neutral-400 mt-1 tabular-nums">{meta}</p>}
    </div>
    {onAdd && (
      <button type="button" onClick={onAdd} className="adm-btn adm-btn-sm shrink-0" data-testid={addTestid}>
        <Plus size={12} /> {addLabel}
      </button>
    )}
  </div>
);

const TicketTable = ({ tickets, computed, scen, onRow, testPrefix, empty, t }) => {
  const totSolgt = tickets.reduce((a, tk) => a + (((computed.ticketCalc[tk.id] || {}).sold || {})[scen] || 0), 0);
  const totNetto = tickets.reduce((a, tk) => a + (((computed.ticketCalc[tk.id] || {}).revenue || {})[scen] || 0), 0);
  return (
  <div className="overflow-x-auto adm-gruppe">
    <table className="adm-table adm-table-flat">
      <thead>
        <tr>
          <th>{t("Product")}</th>
          <th className="adm-right">{t("Price ex VAT")}</th>
          <th className="adm-right">{t("Capacity")}</th>
          <th className="adm-right">{t("Sell-through")}</th>
          <th className="adm-right">{t("Sold")}</th>
          <th className="adm-right">{t("Net revenue")}</th>
        </tr>
      </thead>
      <tbody>
        {tickets.map((tk) => {
          const calc = computed.ticketCalc[tk.id] || { sold: {}, revenue: {} };
          return (
            <tr key={tk.id} onClick={() => onRow(tk)} className="cursor-pointer" data-testid={`${testPrefix}-row-${tk.id}`}>
              <td className="font-medium">{tk.name}</td>
              <td className="text-right tabular-nums">{nok(tk.price)}</td>
              <td className="text-right tabular-nums text-neutral-500">{nok(tk.capacity)}</td>
              <td className="text-right tabular-nums text-neutral-500">{(tk.sellThrough || {})[scen] ?? 0}%</td>
              <td className="text-right tabular-nums">{nok((calc.sold || {})[scen] || 0)}</td>
              <td className="text-right tabular-nums font-semibold">{nok((calc.revenue || {})[scen] || 0)}</td>
            </tr>
          );
        })}
        {tickets.length === 0 && (
          <tr><td colSpan={6} className="text-center text-xs text-neutral-400 py-6">{empty}</td></tr>
        )}
      </tbody>
      {tickets.length > 0 && (
        <tfoot>
          <tr data-testid={`${testPrefix}-total`}>
            <td>{t("Sum")}</td>
            <td /><td /><td />
            <td className="text-right tabular-nums">{nok(totSolgt)}</td>
            <td className="text-right tabular-nums">{nok(totNetto)}</td>
          </tr>
        </tfoot>
      )}
    </table>
  </div>
  );
};

export default function Revenue({ data, scen, actions }) {
  const { t } = useLang();
  const { tickets, sponsors, lines, computed } = data;
  const s = computed.scenarios[scen];
  const inv = computed.inventory;
  const revLines = lines.filter((l) => l.kind === "revenue_other");
  const scenDoc = data.scenarios.find((x) => x.key === scen) || {};
  const [ticketModal, setTicketModal] = useState(null);
  const [sponsorModal, setSponsorModal] = useState(null);
  const [lineDrawer, setLineDrawer] = useState(null);

  const byChannel = (ch) => tickets.filter((tk) => (tk.channel || "physical") === ch);
  const sumSponsorVerdi = sponsors.reduce((a, sp) => a + (sp.value || 0), 0);

  return (
    <div data-testid="budget-revenue" className="adm-rise space-y-14">
      {/* 01 Physical */}
      <section data-testid="rev-physical">
        <SectionHead
          idx="01" title={t("Physical summit")}
          meta={`${kompakt(s.revenue.physical)} ${t("net")} · ${s.physical.sold} ${t("of")} ${s.physical.sellable} ${t("sellable")} (${s.physical.sellThroughPct}%) · ${inv.physical.allocated} ${t("allocated")}${inv.physical.unallocated !== 0 ? ` · ${inv.physical.unallocated} ${t("unallocated")}` : ""}`}
          onAdd={() => setTicketModal({ channel: "physical" })} addLabel={t("Add product")} addTestid="rev-physical-add-btn"
        />
        <TicketTable tickets={byChannel("physical")} computed={computed} scen={scen} onRow={setTicketModal} testPrefix="rev-physical" empty={t("No physical products.")} t={t} />
      </section>

      {/* 02 Digital */}
      <section data-testid="rev-digital">
        <SectionHead
          idx="02" title={t("Digital")}
          meta={`${kompakt(s.revenue.digital)} ${t("net")} · ${s.digital.sold} ${t("passes sold")} · ${t("soft pool, no venue constraint")}`}
          onAdd={() => setTicketModal({ channel: "digital" })} addLabel={t("Add product")} addTestid="rev-digital-add-btn"
        />
        <TicketTable tickets={byChannel("digital")} computed={computed} scen={scen} onRow={setTicketModal} testPrefix="rev-digital" empty={t("No digital products.")} t={t} />
      </section>

      {/* 03 Dinner */}
      <section data-testid="rev-dinner">
        <SectionHead
          idx="03" title={t("Summit dinner")}
          meta={`${kompakt(s.revenue.dinner)} ${t("net")} · ${s.dinner.addonsSold} ${t("add-ons sold")} · ${inv.dinner.capacity} ${t("capacity")} − ${inv.dinner.includedExecutive} ${t("Executive")} − ${inv.dinner.partnerSpeakerAllocation} ${t("partner/speaker")} = ${inv.dinner.sellable} ${t("sellable")} · ${s.dinner.guests} ${t("guests total")}`}
          onAdd={() => setTicketModal({ channel: "dinner" })} addLabel={t("Add product")} addTestid="rev-dinner-add-btn"
        />
        <TicketTable tickets={byChannel("dinner")} computed={computed} scen={scen} onRow={setTicketModal} testPrefix="rev-dinner" empty={t("No dinner add-ons.")} t={t} />
      </section>

      {/* 04 Partners */}
      <section data-testid="rev-partners">
        <SectionHead
          idx="04" title={t("Partners")}
          onAdd={() => setSponsorModal("new")} addLabel={t("Add sponsor")} addTestid="rev-sponsor-add-btn"
        />
        <div className="adm-strip grid-cols-2 md:grid-cols-4 mb-4" data-testid="rev-partner-summary">
          {[
            [t("Signed"), computed.sponsorSummary.signed, "text-neutral-900"],
            [t("Weighted pipeline"), computed.sponsorSummary.weightedPipeline, "text-neutral-500"],
            [t("Target"), computed.sponsorSummary.target || 0, "text-neutral-400"],
            [`${t("Used in")} ${scen}`, s.revenue.sponsors, "text-neutral-900"],
          ].map(([label, sum, tone]) => (
            <div key={label}>
              <p className={`bud-num text-[21px] tabular-nums leading-none ${tone}`}>{kompakt(sum)}</p>
              <p className="mt-2.5 text-[9.5px] font-semibold uppercase tracking-[0.18em] text-neutral-400">{label}</p>
            </div>
          ))}
        </div>
        {(data.settings.partnerTargets || []).length > 0 && (
          <div className="adm-gruppe pt-4 mb-4" data-testid="rev-partner-targets">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400">{t("Commercial target architecture")}</p>
              <p className="text-[11px] text-neutral-400 tabular-nums">
                {kompakt(computed.sponsorSummary.signed)} {t("signed of")} {kompakt(computed.sponsorSummary.target || 0)} {t("target")}
              </p>
            </div>
            <div className="adm-strip grid-cols-2 lg:grid-cols-5">
              {(data.settings.partnerTargets || []).map((tg) => (
                <div key={tg.role}>
                  <p className="bud-num text-[16px] tabular-nums leading-none">{kompakt(tg.qty * tg.amount)}</p>
                  <p className="mt-1.5 text-[9.5px] uppercase tracking-[0.1em] text-neutral-500 leading-snug">{tg.qty > 1 ? `${tg.qty} × ` : ""}{tg.role}</p>
                </div>
              ))}
            </div>
            <p className="text-[10.5px] text-neutral-400 mt-2.5">
              {t("Target ≠ forecast — forecast is calculated from the real pipeline below. Scenario assumptions live under Scenarios.")}
            </p>
          </div>
        )}
        <p className="text-[10.5px] text-neutral-400 mb-2">
          {t("Scenario uses")} <b>{t(MODE_LABEL[scenDoc.sponsorMode] || scenDoc.sponsorMode)}</b>. {t("Agreements are shared with the Partners module.")}
        </p>
        <div className="overflow-x-auto adm-gruppe">
          <table className="adm-table adm-table-flat">
            <thead>
              <tr>
                <th>{t("Sponsor")}</th>
                <th>{t("Package")}</th>
                <th className="adm-right">{t("Value")}</th>
                <th>{t("Stage")}</th>
                <th>{t("Next step")}</th>
                <th className="adm-right">{t("Probability")}</th>
                <th className="adm-right">{t("Weighted")}</th>
                <th className="adm-right">{t("Committed")}</th>
              </tr>
            </thead>
            <tbody>
              {sponsors.map((sp) => {
                const calc = computed.sponsorCalc[sp.id] || {};
                const forfalt = sp.nextStepDue && sp.nextStepDue < new Date().toISOString().slice(0, 10);
                return (
                  <tr key={sp.id} onClick={() => setSponsorModal(sp)} className="cursor-pointer" data-testid={`rev-sponsor-row-${sp.id}`}>
                    <td className="font-medium">{sp.name}</td>
                    <td className="text-neutral-500">{sp.packageName || "—"}</td>
                    <td className="text-right tabular-nums">{nok(sp.value)}</td>
                    <td>
                      <span className={`bud-mono inline-flex items-center gap-1.5 text-[9.5px] uppercase tracking-[0.14em] ${
                        sp.stage === "signed" ? "text-neutral-900" : "text-neutral-400"
                      }`}>
                        <span className={`w-[5px] h-[5px] rounded-full shrink-0 ${sp.stage === "signed" ? "bg-emerald-500" : "bg-neutral-400/70"}`} />
                        {t(STAGE_LABEL[sp.stage] || sp.stage)}
                      </span>
                    </td>
                    <td className="max-w-[200px]" data-testid={`rev-sponsor-nextstep-${sp.id}`}>
                      {sp.nextStep ? (
                        <>
                          <span className="block text-[12px] text-neutral-700 leading-snug truncate">{sp.nextStep}</span>
                          {sp.nextStepDue && (
                            <span className={`bud-mono text-[9px] uppercase tracking-[0.1em] ${forfalt ? "text-red-500" : "text-neutral-400"}`}>
                              {new Date(`${sp.nextStepDue}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}{forfalt ? " · overdue" : ""}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-neutral-300">—</span>
                      )}
                    </td>
                    <td className="text-right tabular-nums text-neutral-500">
                      {calc.probability}%{sp.probabilityOverride != null && <span className="text-amber-600"> *</span>}
                    </td>
                    <td className="text-right tabular-nums">{nok(calc.weighted)}</td>
                    <td className="text-right tabular-nums font-semibold">{calc.committed ? nok(calc.committed) : "—"}</td>
                  </tr>
                );
              })}
              {sponsors.length === 0 && (
                <tr><td colSpan={8} className="text-center text-xs text-neutral-400 py-6">{t("No sponsors in pipeline.")}</td></tr>
              )}
            </tbody>
            {sponsors.length > 0 && (
              <tfoot>
                <tr data-testid="rev-sponsor-total">
                  <td>{t("Sum")}</td>
                  <td />
                  <td className="text-right tabular-nums">{nok(sumSponsorVerdi)}</td>
                  <td /><td /><td />
                  <td className="text-right tabular-nums">{nok(computed.sponsorSummary.weightedPipeline)}</td>
                  <td className="text-right tabular-nums">{nok(computed.sponsorSummary.signed)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>

      {/* 05 Other revenue */}
      <section data-testid="rev-other">
        <SectionHead
          idx="05" title={t("Other revenue")}
          meta={`${t("Grants, side events etc.")} · ${kompakt(s.revenue.other)} ${t("Used in").toLowerCase()} ${scen}`}
          onAdd={() => setLineDrawer("new")} addLabel={t("Add line")} addTestid="rev-other-add-btn"
        />
        <div className="overflow-x-auto adm-gruppe">
          <table className="adm-table adm-table-flat">
            <thead>
              <tr>
                <th>{t("Name")}</th>
                <th className="adm-right">{t("Budget")}</th>
                <th className="adm-right">{t("Forecast")} ({scen})</th>
                <th className="adm-right">{t("Actual")}</th>
              </tr>
            </thead>
            <tbody>
              {revLines.map((l) => (
                <tr key={l.id} onClick={() => setLineDrawer(l)} className="cursor-pointer" data-testid={`rev-other-row-${l.id}`}>
                  <td className="font-medium">{l.name}</td>
                  <td className="text-right tabular-nums text-neutral-500">{nok(l.budget)}</td>
                  <td className="text-right tabular-nums font-semibold">{nok((computed.lineForecasts[l.id] || {})[scen] || 0)}</td>
                  <td className="text-right tabular-nums text-neutral-500">{nok(l.actual)}</td>
                </tr>
              ))}
              {revLines.length === 0 && (
                <tr><td colSpan={4} className="text-center text-xs text-neutral-400 py-6">{t("No other revenue lines.")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {ticketModal && (
        <TicketModal
          ticket={ticketModal.id ? ticketModal : null}
          presetChannel={ticketModal.id ? null : ticketModal.channel}
          computed={computed}
          onSave={async (payload) => {
            if (ticketModal.id) await actions.patchTicket(ticketModal.id, payload);
            else await actions.newTicket(payload);
            setTicketModal(null);
          }}
          onDelete={
            ticketModal.id
              ? async () => {
                  if (window.confirm(`${t("Delete")} “${ticketModal.name}”?`)) {
                    await actions.deleteTicket(ticketModal.id);
                    setTicketModal(null);
                  }
                }
              : null
          }
          onClose={() => setTicketModal(null)}
        />
      )}
      {sponsorModal && (
        <SponsorModal
          sponsor={sponsorModal === "new" ? null : sponsorModal}
          members={data.members}
          settings={data.settings}
          onSave={async (payload) => {
            if (sponsorModal === "new") await actions.newSponsor(payload);
            else await actions.patchSponsor(sponsorModal.id, payload);
            setSponsorModal(null);
          }}
          onDelete={
            sponsorModal !== "new"
              ? async () => {
                  if (window.confirm(`${t("Delete")} “${sponsorModal.name}”?`)) {
                    await actions.deleteSponsor(sponsorModal.id);
                    setSponsorModal(null);
                  }
                }
              : null
          }
          onClose={() => setSponsorModal(null)}
        />
      )}
      {lineDrawer && (
        <LineDrawer
          line={lineDrawer === "new" ? null : lineDrawer}
          kind="revenue_other"
          members={data.members}
          onSave={async (payload) => {
            if (lineDrawer === "new") await actions.newLine({ ...payload, kind: "revenue_other" });
            else await actions.patchLine(lineDrawer.id, payload);
            setLineDrawer(null);
          }}
          onDelete={
            lineDrawer !== "new"
              ? async () => {
                  if (window.confirm(t("Delete this line?"))) {
                    await actions.deleteLine(lineDrawer.id);
                    setLineDrawer(null);
                  }
                }
              : null
          }
          onClose={() => setLineDrawer(null)}
        />
      )}
    </div>
  );
}
